import type { CSSProperties, FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight, Clock, Headphones, Mail, MapPin, Presentation } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Eyebrow } from '@/components/generals/eyebrow'
import { Container } from '@/components/generals/container'
import { FlowLines } from '@/components/generals/flow-lines'
import { ContactForm } from '@/components/forms/contact-form'
import { siteConfig } from '@/constants/site'

const delay = (ms: number): CSSProperties => ({ '--rise-delay': `${ms}ms` } as CSSProperties)

const CHANNELS = [
    { label: 'General enquiries', value: siteConfig.contact.email, href: `mailto:${siteConfig.contact.email}`, Icon: Mail },
    { label: 'Sales', value: siteConfig.contact.salesEmail, href: `mailto:${siteConfig.contact.salesEmail}`, Icon: Presentation },
    { label: 'Customer support', value: siteConfig.contact.supportEmail, href: `mailto:${siteConfig.contact.supportEmail}`, Icon: Headphones },
    { label: 'Office', value: siteConfig.contact.location, href: null, Icon: MapPin },
    { label: 'Hours', value: siteConfig.contact.hours, href: null, Icon: Clock }
]

const Contact: FC = (): ReactNode => {
    return (
        <Layout cta={false}>
            <Seo
                title='Contact'
                description='Get in touch with the Veyra team about sales, support, partnerships or press. We reply within one business day.'
            />

            <section className='relative overflow-hidden bg-cream'>
                <div aria-hidden='true' className='pointer-events-none absolute inset-0 opacity-60'>
                    <FlowLines variant='waves' />
                </div>

                <Container className='relative pb-24 pt-36 md:pb-32 md:pt-44'>
                    <div className='grid gap-16 lg:grid-cols-12 lg:gap-10'>
                        <div className='lg:col-span-5'>
                            <div className='animate-rise' style={delay(40)}>
                                <Eyebrow>Contact</Eyebrow>
                            </div>

                            <h1 className='animate-rise mt-7 font-heading text-display-xl font-medium text-burgundy' style={delay(120)}>
                                Let&rsquo;s talk about <span className='text-stone'>your numbers.</span>
                            </h1>

                            <p className='animate-rise mt-7 max-w-md text-lg leading-relaxed text-stone-dark' style={delay(220)}>
                                Questions about the product, pricing or a partnership? Send us a note and a finance specialist will get back to you within one business day.
                            </p>

                            <ul className='animate-rise mt-12 border-t border-burgundy/10' style={delay(300)}>
                                {CHANNELS.map(({ label, value, href, Icon }) => (
                                    <li key={label} className='flex items-start gap-4 border-b border-burgundy/10 py-5'>
                                        <span className='flex size-10 shrink-0 items-center justify-center rounded-full bg-burgundy text-gold'>
                                            <Icon aria-hidden='true' className='size-4' />
                                        </span>

                                        <span>
                                            <span className='block font-mono text-[10.5px] font-medium uppercase tracking-[0.18em] text-stone'>{label}</span>

                                            {href ? (
                                                <a href={href} className='mt-1 block font-medium text-burgundy underline-offset-4 hover:underline'>{value}</a>
                                            ) : (
                                                <span className='mt-1 block font-medium text-burgundy'>{value}</span>
                                            )}
                                        </span>
                                    </li>
                                ))}
                            </ul>

                            <Link
                                href='/demo'
                                className='animate-rise group mt-10 inline-flex items-center gap-2 border-b border-burgundy/30 pb-1 text-sm font-medium text-burgundy transition-colors hover:border-burgundy'
                                style={delay(360)}
                            >
                                Prefer a walkthrough? Request a demo
                                <ArrowRight aria-hidden='true' className='size-4 transition-transform duration-300 group-hover:translate-x-0.5' />
                            </Link>
                        </div>

                        <div className='animate-rise lg:col-span-6 lg:col-start-7' style={delay(260)}>
                            <div className='rounded-[28px] border border-burgundy/10 bg-paper/90 p-6 shadow-[0_50px_100px_-60px_rgb(57_26_24/0.5)] backdrop-blur sm:p-10'>
                                <h2 className='font-heading text-2xl font-medium text-burgundy'>Send us a message</h2>
                                <p className='mt-2 text-sm text-stone-dark'>All fields are required unless marked optional.</p>

                                <div className='mt-8'>
                                    <ContactForm />
                                </div>
                            </div>
                        </div>
                    </div>
                </Container>
            </section>
        </Layout>
    )
}

export default Contact
